import { useCallback, useEffect, useRef } from 'react';
import {
  DependencyCondition,
  FieldDependency,
  EnhancedFieldConfig,
  EnhancedSection,
  EnhancedGroupedSection,
  isEnhancedGroupedSection,
  OperatorType,
} from '../../types/form/schema.types';
import { FormDataType } from '@tw/tw-component-library';
import { createLogger } from '../../../../../utils/logger';

/**
 * Describes a legacy dependency object where fields depend on a single condition
 * instead of an array of multiple conditions.
 */
export interface LegacyFieldDependency {
  field: string;
  type: string;
  condition: {
    operator: OperatorType;
    value: unknown;
  };
}

/**
 * Type guard function that distinguishes a legacy dependency from a modern one.
 *
 * @param dependency - The dependency object to examine
 * @returns True if the dependency is in the legacy format, false otherwise
 */
const isLegacyDependency = (
  dependency: FieldDependency | LegacyFieldDependency
): dependency is LegacyFieldDependency => {
  return (
    dependency &&
    typeof dependency === 'object' &&
    'field' in dependency &&
    'condition' in dependency &&
    typeof dependency.condition === 'object'
  );
};

/**
 * Custom hook that processes field dependencies. Provides multiple utility methods to:
 * 1. Evaluate conditions.
 * 2. Sort fields by dependency order.
 * 3. Recursively process and update fields or sections based on dependencies.
 *
 * @param formData - The form data containing user inputs or default values
 * @returns An object with methods to evaluate and apply dependencies
 */
export const useDependencyProcessor = (formData: FormDataType = {}) => {
  // Logger for debug / trace statements
  const logger = createLogger('DependencyProcessor');

  // Track previous form data for detecting changes
  const prevFormDataRef = useRef<FormDataType>({});

  // Track fields that depend on conditional fields
  const dependentFieldsRef = useRef<Set<string>>(new Set());

  // REMOVED: Version counter approach caused infinite loops
  // Instead, we'll rely on formData value changes to trigger recalculation
  // The schema memoization should depend on actual formData values, not a version counter

  // CRITICAL: Use a ref to always access the latest formData, preventing stale closures in production
  // This ensures that even if React optimizes re-renders, we always have the latest data
  // IMPORTANT: Update synchronously during render, NOT in useEffect, to avoid timing issues
  const formDataRef = useRef<FormDataType>(formData);

  // CRITICAL: Track pending updates that haven't been reflected in the prop yet
  // When updateFormData is called, it updates state asynchronously, but we need the ref updated immediately
  // This allows FormField updates to be seen by dependency processing before React re-renders
  const pendingUpdatesRef = useRef<Partial<FormDataType>>({});

  // Function to merge pending updates into the ref synchronously
  // CRITICAL: Don't clear pendingUpdatesRef here - keep accumulating until prop confirms them
  const mergePendingUpdates = useCallback(() => {
    if (Object.keys(pendingUpdatesRef.current).length > 0) {
      // Merge pending updates into ref, but preserve any existing ref values that are newer
      formDataRef.current = {
        ...formDataRef.current,
        ...pendingUpdatesRef.current,
      };
      logger.state('[DependencyProcessor] 🔄 Merged pending updates', {
        pendingUpdates: pendingUpdatesRef.current,
        updatedRefValue: Object.fromEntries(
          Object.entries(formDataRef.current).slice(0, 10)
        ),
      });
    }
  }, [logger]);

  // Function to clear pending updates that are now confirmed in the prop
  const clearConfirmedPendingUpdates = useCallback(() => {
    if (Object.keys(pendingUpdatesRef.current).length > 0) {
      // Check which pending updates are now confirmed in the prop
      const confirmedKeys: string[] = [];
      for (const key in pendingUpdatesRef.current) {
        if (formData[key] === pendingUpdatesRef.current[key]) {
          confirmedKeys.push(key);
        }
      }
      // Remove confirmed updates from pending
      if (confirmedKeys.length > 0) {
        const remaining = { ...pendingUpdatesRef.current };
        confirmedKeys.forEach((key) => delete remaining[key]);
        pendingUpdatesRef.current = remaining;
        logger.state(
          '[DependencyProcessor] ✅ Cleared confirmed pending updates',
          {
            confirmedKeys,
            remainingPending: Object.keys(remaining),
          }
        );
      }
    }
  }, [formData, logger]);

  // Update ref immediately on every render (synchronously) to ensure latest data is always available
  // This prevents stale closures where dependency evaluation happens before useEffect runs
  // CRITICAL: Merge pending updates FIRST, then update with prop, but preserve pending values that aren't in prop yet
  const prevRefData = formDataRef.current;

  // Merge pending updates into ref (keeps pending values)
  mergePendingUpdates();

  // Update ref with prop, but pending updates take precedence for keys that exist in pending
  formDataRef.current = {
    ...formData,
    ...pendingUpdatesRef.current, // Pending updates override prop (they're newer)
  };

  // Clear pending updates that are now confirmed in the prop
  clearConfirmedPendingUpdates();

  // PRODUCTION DEBUG: Log ref updates to track data flow
  if (prevRefData !== formData) {
    logger.state('[DependencyProcessor] 🔄 Ref updated', {
      timestamp: new Date().toISOString(),
      isNewReference: prevRefData !== formData,
      formDataKeys: Object.keys(formData || {}),
      refKeys: Object.keys(formDataRef.current || {}),
      sampleField:
        formData?.payments_count !== undefined
          ? { payments_count: formData.payments_count }
          : 'no payments_count',
      refSampleField:
        formDataRef.current?.payments_count !== undefined
          ? { payments_count: formDataRef.current.payments_count }
          : 'no payments_count',
    });
  }

  /**
   * Logs form data changes that might affect dependencies when the hook runs or formData updates.
   * Identifies specific changes that could affect dependency visibility.
   */
  useEffect(() => {
    const changedFields = Object.keys(formData).filter(
      (key) => formData[key] !== prevFormDataRef.current[key]
    );

    if (changedFields.length > 0) {
      logger.state('Form data updated, dependencies need re-evaluation', {
        changedFields,
        formDataKeys: Object.keys(formData),
      });

      // Check for fields that are known to affect visibility
      const visibilityTriggers = ['payments_count'];
      const hasVisibilityChanges = changedFields.some((field) =>
        visibilityTriggers.includes(field)
      );

      if (hasVisibilityChanges) {
        logger.debug('Visibility-affecting field changed', {
          changedVisibilityFields: changedFields.filter((f) =>
            visibilityTriggers.includes(f)
          ),
          value: formData.payments_count,
          affectedFields: Array.from(dependentFieldsRef.current),
        });
      }

      // Update ref to current data for next comparison
      prevFormDataRef.current = { ...formData };
    }
  }, [formData, logger]);

  /**
   * Evaluates a single condition by comparing a field's current value against the specified operator and value.
   *
   * @param fieldValue - The current value of the target field
   * @param operator - The operator to use (e.g., 'equals', 'notEquals', 'contains')
   * @param value - The value to compare against fieldValue
   * @returns True if the condition is satisfied, false otherwise
   */
  const evaluateConditionValue = useCallback(
    (fieldValue: unknown, operator: OperatorType, value: unknown): boolean => {
      // Log the actual comparison for better debugging
      logger.debug(
        `Evaluating condition: ${String(fieldValue)} ${operator} ${String(
          value
        )}`,
        {
          fieldValueType: typeof fieldValue,
          valueType: typeof value,
        }
      );

      switch (operator) {
        case 'equals':
          return fieldValue === value;
        case 'notEquals':
          return fieldValue !== value;
        case 'contains':
          return Array.isArray(fieldValue)
            ? fieldValue.includes(value)
            : typeof fieldValue === 'string' &&
                fieldValue.includes(String(value));
        case 'notContains':
          return Array.isArray(fieldValue)
            ? !fieldValue.includes(value)
            : typeof fieldValue === 'string' &&
                !fieldValue.includes(String(value));
        case 'greaterThan':
          return Number(fieldValue) > Number(value);
        case 'lessThan':
          return Number(fieldValue) < Number(value);
        case 'isEmpty':
          return (
            fieldValue === undefined ||
            fieldValue === null ||
            fieldValue === '' ||
            (Array.isArray(fieldValue) && fieldValue.length === 0)
          );
        case 'isNotEmpty':
          return (
            fieldValue !== undefined &&
            fieldValue !== null &&
            fieldValue !== '' &&
            (!Array.isArray(fieldValue) || fieldValue.length > 0)
          );
        default:
          logger.warning(`Unknown operator: ${operator}`, {
            fieldValue,
            value,
          });
          return false;
      }
    },
    [logger]
  );

  /**
   * Evaluates a single condition object using the form data.
   *
   * @param condition - Contains {field, operator, value} to evaluate
   * @returns True if the condition is satisfied, false otherwise
   */
  const evaluateCondition = useCallback(
    (condition: DependencyCondition): boolean => {
      const { field, operator, value } = condition;
      // Use ref to get latest formData to prevent stale closures in production
      const currentFormData = formDataRef.current;
      const fieldValue = currentFormData?.[field];

      // PRODUCTION DEBUG: Log condition evaluation with data source info
      logger.state('[DependencyProcessor] 🔍 Evaluating condition', {
        timestamp: new Date().toISOString(),
        field,
        operator,
        expectedValue: value,
        actualFieldValue: fieldValue,
        fieldExists: field in (currentFormData || {}),
        formDataKeys: Object.keys(currentFormData || {}),
        refHasData: !!formDataRef.current,
        refKeys: Object.keys(formDataRef.current || {}),
        // Compare with direct formData prop to detect stale data
        propHasField: field in (formData || {}),
        propFieldValue: formData?.[field],
        valuesMatch: formData?.[field] === formDataRef.current?.[field],
      });

      const result = evaluateConditionValue(fieldValue, operator, value);

      logger.state('[DependencyProcessor] ✅ Condition result', {
        field,
        operator,
        value,
        fieldValue,
        result,
        evaluation: `${fieldValue} ${operator} ${value} = ${result}`,
      });

      logger.debug(`Evaluating condition: ${field} ${operator} ${value}`, {
        fieldValue,
        result,
        fieldInFormData: field in currentFormData,
      });
      return result;
    },
    [evaluateConditionValue, logger, formData]
  );

  /**
   * Evaluates a dependency, which may be in legacy format or modern format.
   *  - Legacy dependencies have a single condition property.
   *  - Modern dependencies have multiple conditions and a logicalOperator.
   *
   * @param dependency - The dependency object to evaluate
   * @returns True if all or any conditions are satisfied based on logicalOperator, false otherwise
   */
  const evaluateDependency = useCallback(
    (dependency: FieldDependency | LegacyFieldDependency): boolean => {
      // Use ref to get latest formData to prevent stale closures in production
      const currentFormData = formDataRef.current;

      logger.state('[DependencyProcessor] 🎯 Evaluating dependency', {
        timestamp: new Date().toISOString(),
        dependencyType: dependency.type,
        isLegacy: isLegacyDependency(dependency),
        currentFormDataKeys: Object.keys(currentFormData || {}),
        refDataKeys: Object.keys(formDataRef.current || {}),
      });

      // Legacy format
      if (isLegacyDependency(dependency)) {
        const fieldValue = currentFormData?.[dependency.field];
        const { operator, value } = dependency.condition;

        const result = evaluateConditionValue(fieldValue, operator, value);

        logger.state('[DependencyProcessor] 📦 Legacy dependency result', {
          dependencyType: dependency.type,
          field: dependency.field,
          operator,
          value,
          fieldValue,
          result,
          refHasField: dependency.field in (formDataRef.current || {}),
        });

        logger.debug(
          `Evaluating legacy dependency: ${dependency.field} ${operator} ${value}`,
          {
            fieldValue,
            result,
            dependencyType: dependency.type,
          }
        );
        return result;
      }

      // Modern format
      const { conditions = [], logicalOperator = 'AND' } = dependency;
      if (conditions.length === 0) {
        logger.state(
          '[DependencyProcessor] ⚠️ Empty conditions array, returning true'
        );
        return true;
      }

      // Track fields used in conditions for future updates
      conditions.forEach((condition) => {
        dependentFieldsRef.current.add(condition.field);
      });

      logger.state('[DependencyProcessor] 🔗 Processing modern dependency', {
        conditionsCount: conditions.length,
        logicalOperator,
        conditions: conditions.map(
          (c) => `${c.field} ${c.operator} ${c.value}`
        ),
        fieldsInRef: conditions.map((c) => ({
          field: c.field,
          inRef: c.field in (formDataRef.current || {}),
          value: formDataRef.current?.[c.field],
        })),
      });

      let result: boolean;
      if (logicalOperator === 'AND') {
        result = conditions.every((condition) => evaluateCondition(condition));
      } else {
        result = conditions.some((condition) => evaluateCondition(condition));
      }

      logger.state('[DependencyProcessor] ✅ Dependency final result', {
        dependencyType: dependency.type,
        logicalOperator,
        conditionsCount: conditions.length,
        result,
        allConditions: conditions.map((c) => ({
          field: c.field,
          operator: c.operator,
          value: c.value,
          fieldValue: formDataRef.current?.[c.field],
        })),
      });

      logger.debug(`Evaluating dependency with ${logicalOperator} logic`, {
        conditions: conditions.map(
          (c) => `${c.field} ${c.operator} ${c.value}`
        ),
        result,
        dependencyType: dependency.type,
      });
      return result;
    },
    [evaluateCondition, evaluateConditionValue, logger]
  );

  /**
   * Applies the result of a dependency evaluation to a specific field.
   * For example, if a dependency is of type "hide" and result is true,
   * the field's hidden property will be set to true.
   *
   * @param field - The field whose properties need to be updated
   * @param dependency - The dependency indicating how the field should be updated
   * @param result - The boolean result of the dependency evaluation
   * @returns A new field object with updated properties
   */
  const applyDependencyResult = useCallback(
    (
      field: EnhancedFieldConfig,
      dependency: FieldDependency | LegacyFieldDependency,
      result: boolean
    ): EnhancedFieldConfig => {
      const updatedField = { ...field };
      const type = dependency.type;

      logger.state(`Applying dependency result to field: ${field.name}`, {
        dependencyType: type,
        result,
        fieldBefore: {
          hidden: field.hidden,
          disabled: field.disabled,
          required: field.required,
        },
      });

      switch (type) {
        case 'show':
          updatedField.hidden = !result;
          // Track visibility changes for special handling
          if (field.name && updatedField.hidden !== field.hidden) {
            logger.debug(
              `Visibility changed for ${field.name}: now ${
                updatedField.hidden ? 'hidden' : 'visible'
              }`
            );
          }
          break;
        case 'hide':
          updatedField.hidden = result;
          // Track visibility changes for special handling
          if (field.name && updatedField.hidden !== field.hidden) {
            logger.debug(
              `Visibility changed for ${field.name}: now ${
                updatedField.hidden ? 'hidden' : 'visible'
              }`
            );
          }
          break;
        case 'enable':
          updatedField.disabled = !result;
          break;
        case 'disable':
          updatedField.disabled = result;
          break;
        case 'require':
          updatedField.required = result;
          break;
      }

      // Only log if there were changes
      if (
        updatedField.hidden !== field.hidden ||
        updatedField.disabled !== field.disabled ||
        updatedField.required !== field.required
      ) {
        logger.state('Field updated after dependency application', {
          fieldAfter: {
            hidden: updatedField.hidden,
            disabled: updatedField.disabled,
            required: updatedField.required,
          },
          changed: {
            hidden: updatedField.hidden !== field.hidden,
            disabled: updatedField.disabled !== field.disabled,
            required: updatedField.required !== field.required,
          },
        });
      }

      return updatedField;
    },
    [logger]
  );

  /**
   * Processes all dependencies for a given field by evaluating each dependency
   * and applying the result to the field's properties (e.g., show, hide, enable, disable).
   *
   * @param field - The enhanced field configuration with optional dependencies
   * @returns A new field object with all dependencies processed
   */
  const processFieldDependencies = useCallback(
    (field: EnhancedFieldConfig): EnhancedFieldConfig => {
      if (!field.dependencies || field.dependencies.length === 0) {
        return field;
      }

      // Use ref to get latest formData to prevent stale closures
      const currentFormData = formDataRef.current;

      logger.state(`Processing dependencies for field: ${field.name}`, {
        value: currentFormData[field.name],
        dependencies: field.dependencies?.map((dep) => ({
          type: dep.type,
          conditions: 'conditions' in dep ? dep.conditions : null,
        })),
        initialState: {
          hidden: field.hidden,
          disabled: field.disabled,
          required: field.required,
        },
      });

      let processedField = { ...field };

      for (const dependency of field.dependencies) {
        const typedDependency = dependency as
          | FieldDependency
          | LegacyFieldDependency;
        const result = evaluateDependency(typedDependency);

        // Distinguish log messages for legacy vs. modern
        if (isLegacyDependency(typedDependency)) {
          logger.state(`Legacy dependency evaluation for ${field.name}`, {
            dependsOn: typedDependency.field,
            type: typedDependency.type,
            operator: typedDependency.condition.operator,
            value: typedDependency.condition.value,
            result,
          });
        } else {
          logger.state(`Modern dependency evaluation for ${field.name}`, {
            type: typedDependency.type,
            logicalOperator: typedDependency.logicalOperator,
            conditionsCount: typedDependency.conditions?.length,
            result,
          });
        }

        processedField = applyDependencyResult(
          processedField,
          typedDependency,
          result
        );
      }

      // Check if any properties actually changed
      const hasChanged =
        processedField.hidden !== field.hidden ||
        processedField.disabled !== field.disabled ||
        processedField.required !== field.required;

      if (hasChanged) {
        logger.state(
          `Field dependencies processed with changes: ${field.name}`,
          {
            finalState: {
              hidden: processedField.hidden,
              disabled: processedField.disabled,
              required: processedField.required,
            },
            changed: {
              hidden: processedField.hidden !== field.hidden,
              disabled: processedField.disabled !== field.disabled,
              required: processedField.required !== field.required,
            },
          }
        );
      }

      return processedField;
    },
    [evaluateDependency, applyDependencyResult, logger]
  );

  /**
   * Builds a dependency graph to determine the order in which fields should be processed.
   * This graph maps each field to a list of fields that depend on it.
   *
   * Updated to support cross-section dependencies by not requiring all referenced fields
   * to be in the current field set.
   *
   * @param fields - An array (or single object) of fields
   * @returns A Map object where each key is a field name, and the value is an array of dependent field names
   */
  const buildDependencyGraph = useCallback(
    (
      fields: EnhancedFieldConfig[] | EnhancedFieldConfig
    ): Map<string, string[]> => {
      const graph = new Map<string, string[]>();
      // Convert single object to array if needed
      const fieldsArray = Array.isArray(fields) ? fields : [fields];

      logger.state('Building dependency graph', {
        fieldsCount: fieldsArray.length,
        isArray: Array.isArray(fields),
      });

      // Initialize graph with all fields in current set
      fieldsArray.forEach((field) => {
        if (field.name) {
          graph.set(field.name, []);
        }
      });

      // Populate the graph with edges from dependencies
      fieldsArray.forEach((field) => {
        if (field.dependencies && field.name) {
          field.dependencies.forEach((dep) => {
            const dependency = dep as FieldDependency | LegacyFieldDependency;
            // Legacy format
            if (isLegacyDependency(dependency)) {
              const dependsOn = dependency.field;
              // Initialize the dependency field in graph if it doesn't exist (cross-section support)
              if (!graph.has(dependsOn)) {
                graph.set(dependsOn, []);
                logger.debug(
                  `Cross-section dependency detected: ${field.name} depends on ${dependsOn}`,
                  { field: field.name, dependsOn }
                );
              }
              const dependents = graph.get(dependsOn) || [];
              if (!dependents.includes(field.name)) {
                dependents.push(field.name);
                graph.set(dependsOn, dependents);
              }
            }
            // Modern format
            else if ('conditions' in dependency) {
              dependency.conditions.forEach((condition) => {
                const dependsOn = condition.field;
                // Initialize the dependency field in graph if it doesn't exist (cross-section support)
                if (!graph.has(dependsOn)) {
                  graph.set(dependsOn, []);
                  logger.debug(
                    `Cross-section dependency detected: ${field.name} depends on ${dependsOn}`,
                    { field: field.name, dependsOn }
                  );
                }
                const dependents = graph.get(dependsOn) || [];
                if (!dependents.includes(field.name)) {
                  dependents.push(field.name);
                  graph.set(dependsOn, dependents);
                }
              });
            }
          });
        }
      });

      // Log cross-section dependencies for debugging
      const crossSectionDeps: string[] = [];
      graph.forEach((dependents, fieldName) => {
        const fieldExists = fieldsArray.some((f) => f.name === fieldName);
        if (!fieldExists && dependents.length > 0) {
          crossSectionDeps.push(`${fieldName} -> [${dependents.join(', ')}]`);
        }
      });
      if (crossSectionDeps.length > 0) {
        logger.debug('Cross-section dependencies in graph', {
          dependencies: crossSectionDeps,
        });
      }

      return graph;
    },
    [logger]
  );

  /**
   * Sorts fields in the order they should be processed based on dependencies. Uses a
   * topological sort approach so that a field is processed before the fields that depend on it.
   *
   * Updated to handle cross-section dependencies where the controlling field may not be
   * in the current section being processed.
   *
   * @param fields - An array (or single object) of just the fields to be sorted
   * @returns The fields in a dependency-safe processing order
   */
  const sortFieldsByDependencyOrder = useCallback(
    (
      fields: EnhancedFieldConfig[] | EnhancedFieldConfig
    ): EnhancedFieldConfig[] => {
      const fieldsArray = Array.isArray(fields) ? fields : [fields];
      const graph = buildDependencyGraph(fieldsArray);
      const visited = new Set<string>();
      const result: EnhancedFieldConfig[] = [];

      // Create a set of field names in the current section for quick lookup
      const fieldNamesInSection = new Set(
        fieldsArray.map((f) => f.name).filter(Boolean)
      );

      // Topological sort recursive helper
      const visit = (fieldName: string) => {
        if (visited.has(fieldName)) return;
        visited.add(fieldName);

        const dependents = graph.get(fieldName) || [];
        // Only visit dependents that are in the current section
        dependents
          .filter((dependent) => fieldNamesInSection.has(dependent))
          .forEach((dependent) => visit(dependent));

        // Only add field to result if it exists in current section
        const field = fieldsArray.find((f) => f.name === fieldName);
        if (field) result.push(field);
      };

      // Visit all fields in the current section
      fieldsArray.forEach((field) => {
        if (field.name && !visited.has(field.name)) {
          visit(field.name);
        }
      });

      logger.debug('Fields sorted by dependency order', {
        originalCount: fieldsArray.length,
        sortedCount: result.length,
        hasReordering: result.length > 0,
      });

      // Return the sorted list of fields (not reversed to preserve schema order)
      return result;
    },
    [buildDependencyGraph, logger]
  );

  /**
   * Processes the dependencies of all given fields. It sorts fields by dependency order,
   * evaluates, and applies their dependencies, then returns the processed fields in their
   * original order.
   *
   * @param fields - An array or single EnhancedFieldConfig object
   * @returns The fields with dependencies processed
   */
  // CRITICAL: Use ref to ensure we always have the absolute latest formData
  // This prevents race conditions where formData updates between renders
  const processFields = useCallback(
    (
      fields: EnhancedFieldConfig[] | EnhancedFieldConfig
    ): EnhancedFieldConfig[] => {
      // CRITICAL FIX: Merge pending updates and force ref update RIGHT before processing
      // This handles cases where multiple renders happen in rapid succession
      // AND ensures that updates from FormField (via updateFormData) are seen immediately
      mergePendingUpdates();
      // Update with prop but preserve pending updates (they override prop since they're newer)
      formDataRef.current = {
        ...formData,
        ...pendingUpdatesRef.current,
      };

      logger.state('[DependencyProcessor] 🚀 processFields called', {
        timestamp: new Date().toISOString(),
        fieldsCount: Array.isArray(fields) ? fields.length : 1,
        currentFormDataKeys: Object.keys(formDataRef.current || {}),
        refDataSnapshot: {
          payments_count: formDataRef.current?.payments_count,
          keys: Object.keys(formDataRef.current || {}).slice(0, 10),
        },
        propDataSnapshot: {
          payments_count: formData?.payments_count,
          keys: Object.keys(formData || {}).slice(0, 10),
        },
        // CRITICAL: Verify ref has latest data at processing time
        refMatchesProp: formDataRef.current === formData,
        refValue: formDataRef.current?.payments_count,
        propValue: formData?.payments_count,
        valuesMatch:
          formDataRef.current?.payments_count === formData?.payments_count,
      });

      const fieldsArray = Array.isArray(fields) ? fields : [fields];

      // Sort fields by dependency order (topological sort)
      const sortedFields = sortFieldsByDependencyOrder(fieldsArray);

      // Create a map to store processed fields
      const processedFieldsMap = new Map<string, EnhancedFieldConfig>();
      sortedFields.forEach((field) => {
        if (field.name) {
          processedFieldsMap.set(field.name, processFieldDependencies(field));
        }
      });

      // Track visibility changes for form data updates
      const visibilityChanges: Record<string, boolean> = {};

      // Retain original order but update with processed dependencies
      const processedFields = fieldsArray.map((field) => {
        const processedField =
          field.name && processedFieldsMap.has(field.name)
            ? processedFieldsMap.get(field.name)!
            : processFieldDependencies(field);

        // Track visibility changes
        if (field.name && processedField.hidden !== field.hidden) {
          visibilityChanges[field.name] = !!processedField.hidden;
        }

        return processedField;
      });

      // PRODUCTION DEBUG: Log visibility changes
      if (Object.keys(visibilityChanges).length > 0) {
        logger.state('[DependencyProcessor] 👁️ Visibility changes detected', {
          changes: visibilityChanges,
          fieldsAffected: Object.keys(visibilityChanges),
          timestamp: new Date().toISOString(),
        });
      }

      logger.state('[DependencyProcessor] ✅ processFields completed', {
        processedCount: processedFields.length,
        visibilityChangesCount: Object.keys(visibilityChanges).length,
      });

      // Log visibility changes for debugging
      if (Object.keys(visibilityChanges).length > 0) {
        logger.debug('Field visibility changes detected', {
          changes: visibilityChanges,
        });
      }

      return processedFields;
    },
    [
      sortFieldsByDependencyOrder,
      processFieldDependencies,
      logger,
      formData,
      mergePendingUpdates,
    ]
  );

  /**
   * Recursively processes a section's fields or its sub-sections if it is a grouped section.
   * Also handles section-level dependencies that control the visibility/state of entire sections.
   *
   * @param section - A standard or grouped section object to process
   * @returns A new section (or grouped section) with dependencies fully processed
   */
  const processSection = useCallback(
    (
      section: EnhancedSection | EnhancedGroupedSection
    ): EnhancedSection | EnhancedGroupedSection => {
      let processedSection = { ...section };

      // Process section-level dependencies if they exist
      if (section.dependencies && section.dependencies.length > 0) {
        logger.state(
          `Processing section-level dependencies: ${section.title?.text}`,
          {
            dependencies: section.dependencies.map((dep: FieldDependency) => ({
              type: dep.type,
              conditions: 'conditions' in dep ? dep.conditions : null,
            })),
          }
        );

        let showResult: boolean | undefined;

        for (const dependency of section.dependencies) {
          const typedDependency = dependency as
            | FieldDependency
            | LegacyFieldDependency;
          const result = evaluateDependency(typedDependency);

          logger.debug(`Section dependency result for ${section.title?.text}`, {
            dependencyType: typedDependency.type,
            result,
          });

          // Multiple show rules must all pass. A single show rule is unchanged.
          switch (typedDependency.type) {
            case 'show':
              showResult =
                showResult === undefined ? result : showResult && result;
              break;
            case 'hide':
              processedSection = { ...processedSection, hidden: result };
              break;
            // Note: disable/enable/require don't typically apply to sections
          }
        }

        if (showResult !== undefined) {
          processedSection = { ...processedSection, hidden: !showResult };
        }
      }

      // Process sub-sections or fields based on section type
      if (isEnhancedGroupedSection(processedSection)) {
        return {
          ...processedSection,
          sections: processedSection.sections.map((subSection) =>
            processSection(subSection)
          ),
        };
      }

      return {
        ...processedSection,
        fields: processFields(processedSection.fields),
      };
    },
    [processFields, evaluateDependency, logger]
  );

  /**
   * Processes all sections in a form by applying dependency logic at each level.
   *
   * @param sections - An array of standard or grouped sections
   * @returns A new array of processed sections
   */
  const processSections = useCallback(
    (
      sections: (EnhancedSection | EnhancedGroupedSection)[]
    ): (EnhancedSection | EnhancedGroupedSection)[] => {
      return sections.map((section) => processSection(section));
    },
    [processSection]
  );

  /**
   * Retrieves the names of all fields that depend on a specific field. Recursively searches
   * through grouped sections and standard sections.
   *
   * @param fieldName - The name of the field whose dependents you want to find
   * @param sections - The sections (standard or grouped) to search in
   * @returns An array of names of fields that depend on the specified field
   */
  const getDependentFields = useCallback(
    (
      fieldName: string,
      sections: (EnhancedSection | EnhancedGroupedSection)[]
    ): string[] => {
      let dependentFields: string[] = [];

      sections.forEach((section) => {
        if (isEnhancedGroupedSection(section)) {
          // Recursively search sub-sections
          dependentFields = dependentFields.concat(
            getDependentFields(fieldName, section.sections)
          );
        } else {
          // Search fields in the single section
          section.fields.forEach((field) => {
            if (
              field.dependencies?.some((dep) => {
                const dependency = dep as
                  | FieldDependency
                  | LegacyFieldDependency;

                // Check for legacy or modern format
                if (
                  isLegacyDependency(dependency) &&
                  dependency.field === fieldName
                ) {
                  dependentFields.push(field.name || '');
                  return true;
                }
                if ('conditions' in dependency) {
                  if (
                    dependency.conditions.some(
                      (condition) => condition.field === fieldName
                    )
                  ) {
                    dependentFields.push(field.name || '');
                    return true;
                  }
                }
                return false;
              })
            ) {
              // Field is directly dependent on fieldName
              if (field.name) {
                // Track this dependency for future updates
                dependentFieldsRef.current.add(field.name);
              }
            }
          });
        }
      });

      // Log the dependency relationships
      if (dependentFields.length > 0) {
        logger.debug(`Fields dependent on ${fieldName}:`, {
          dependentFields,
        });
      }

      return dependentFields;
    },
    [logger]
  );

  /**
   * Effect to specially handle form data changes that affect visibility conditions.
   * This provides a more direct way to respond to changes in fields like payments_count.
   */
  useEffect(() => {
    if (formData.payments_count) {
      logger.debug('payments_count changed, checking dependent fields', {
        payments_count: formData.payments_count,
        potentialDependents: ['gridData', 'payment_spreadsheet'],
      });
    }
  }, [formData.payments_count, logger]);

  // CRITICAL: Expose a function to queue pending updates for immediate ref access
  // This is called by updateFormData wrapper to ensure dependency processing sees changes immediately
  // CRITICAL: Don't clear pending updates here - keep accumulating until they're confirmed in the prop
  const queuePendingUpdate = useCallback((update: Partial<FormDataType>) => {
    // Accumulate updates in pending queue
    pendingUpdatesRef.current = {
      ...pendingUpdatesRef.current,
      ...update,
    };
    // Immediately merge into ref so processFields can see it
    formDataRef.current = {
      ...formDataRef.current,
      ...pendingUpdatesRef.current,
    };
    logger.state('[DependencyProcessor] 📥 Queued pending update', {
      update,
      allPendingUpdates: pendingUpdatesRef.current,
      refNowContains: Object.fromEntries(
        Object.entries(formDataRef.current).slice(0, 10)
      ),
    });
    // NOTE: We DON'T clear pendingUpdatesRef here - it will be cleared when the prop confirms the update
  }, []);

  return {
    processFields,
    processSections,
    evaluateCondition,
    evaluateDependency,
    processFieldDependencies,
    getDependentFields,
    // Expose queuePendingUpdate so updateFormData can sync changes immediately
    queuePendingUpdate,
  };
};
